import { Dialog, Grid } from "@material-ui/core";
import { InputDateRange } from "components/DateRangePicker/dateRangePicker";
import { Button, SecondaryButton } from "components/Form";
import { Error } from "components/Form/FormikControls";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";
import { addDays, addMonths, differenceInMilliseconds } from "date-fns";
import { Form, Formik, useFormikContext } from "formik";
import React from "react";
import * as Yup from "yup";
import { SmallText } from "components/Text";

interface FilteringModalProps {
  closeSelf: () => void;
  setFilters: (filters: {
    pickupDateGte?: string | null;
    pickupDateLte?: string | null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic filter properties
    [key: string]: any;
  }) => void;
  filters?: {
    pickupDateGte?: string | null;
    pickupDateLte?: string | null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic filter properties
    [key: string]: any;
  };
}

const validationSchema = Yup.object().shape({
  pickupDateGte: Yup.date().nullable().required("Start date is required"),
  pickupDateLte: Yup.date()
    .nullable()
    .test(
      "withinRange",
      "Maximum date range of 3 months.",
      function (pickupDateLte) {
        const { pickupDateGte } = this.parent;
        if (!pickupDateGte || !pickupDateLte) return true;
        return (
          pickupDateLte instanceof Date &&
          pickupDateGte instanceof Date &&
          // Max of 3 months date range
          pickupDateLte.getTime() - pickupDateGte.getTime() <=
            differenceInMilliseconds(
              addDays(addMonths(new Date(0), 3), 1), // Add a day to handle datetime correctly.
              new Date(0),
            )
        );
      },
    ),
});

function FilterForm({ handleClose }: { handleClose: () => void }) {
  const { setValues, values, errors, isValid, submitForm } =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Filter form values
    useFormikContext<Record<string, any>>();

  return (
    <Dialog
      id="filtering-modal"
      open
      scroll="body"
      maxWidth="md"
      fullWidth
      onClose={handleClose}
    >
      <Form data-tour="filtering-form">
        <DialogTitle onClose={handleClose}>Loads Filter</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {(errors?.pickupDateGte || errors?.pickupDateLte) && (
              <Grid item xs={12}>
                <Error>{errors.pickupDateGte || errors.pickupDateLte}</Error>
              </Grid>
            )}
            <InputDateRange
              updateDates={(pickupDateGte, pickupDateLte) =>
                setValues({ ...values, pickupDateGte, pickupDateLte })
              }
              startDateFieldName="pickupDateGte"
              endDateFieldName="pickupDateLte"
              includeTime={false}
            />
            <Grid item xs={12}>
              <SmallText>
                Note: Historical loads data is limited to 30 days for extended
                access users.
              </SmallText>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={handleClose}>Cancel</SecondaryButton>
          <Button
            data-tour="submit"
            type="submit"
            disabled={!isValid}
            onClick={submitForm}
          >
            Save
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
}

export function FilteringModal({
  closeSelf,
  setFilters,
  filters = {},
}: FilteringModalProps) {
  return (
    <Formik
      initialValues={{
        pickupDateGte: filters.pickupDateGte || null,
        pickupDateLte: filters.pickupDateLte || null,
      }}
      validationSchema={validationSchema}
      onSubmit={values => {
        setFilters(values);
        closeSelf();
      }}
    >
      <FilterForm handleClose={closeSelf} />
    </Formik>
  );
}
